import { useSyncExternalStore } from 'react';
import type { DivisionId, UserRole } from '@atlas/shared';

/**
 * "View as role" — an admin's display-only preview of what a set of roles sees.
 *
 * A plain module store rather than a context: every consumer already funnels
 * through `useHiddenFeatures`, and a provider would have to be threaded through
 * `main.tsx` for one boolean. Kept in sessionStorage so a page reload mid-preview
 * doesn't silently drop back to the admin's own view, and so it dies with the tab.
 *
 * ponytail: DISPLAY ONLY. The API still answers as the real user, so this shows
 * what Feature Visibility hides — not role-based data scoping or server-side
 * denials. Nothing here grants access; it can only take chrome away.
 *
 * A LIST of roles, not one, because that is the case the settings cannot honour:
 * per-role hides resolve all-must-hide, so a user holding two roles keeps
 * anything either can see. Previewing a single role shows a view no such user
 * has, which is how a hide gets configured and believed. Stored as JSON; a value
 * left by the single-role version fails to parse and reads as no preview.
 */
const KEY = 'atlas:preview-role';

const read = (): UserRole[] | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? (parsed as UserRole[]) : null;
  } catch {
    return null;
  }
};

let previewRoles: UserRole[] | null = read();
const listeners = new Set<() => void>();

/** Preview these roles together, or `null` / `[]` to stop previewing. */
export function setPreviewRole(roles: readonly UserRole[] | null): void {
  previewRoles = roles?.length ? [...roles] : null;
  try {
    if (previewRoles) sessionStorage.setItem(KEY, JSON.stringify(previewRoles));
    else sessionStorage.removeItem(KEY);
  } catch {
    /* private mode — the in-memory value still drives this tab */
  }
  listeners.forEach((notify) => notify());
}

function subscribe(notify: () => void): () => void {
  listeners.add(notify);
  return () => listeners.delete(notify);
}

export function usePreviewRole(): UserRole[] | null {
  return useSyncExternalStore(subscribe, () => previewRoles, () => previewRoles);
}

/**
 * The roles the UI should render for: the previewed role while previewing, the
 * user's own otherwise. Nav entries are role-gated as well as visibility-gated,
 * so without this an admin previewing another role still sees Admin-only items
 * that the role could never have.
 *
 * ponytail: display gating only — `ProtectedRoute` and every API call still use
 * the real roles, so this cannot let anyone reach something they could not.
 */
export function useEffectiveRoles(
  realRoles: readonly string[] | undefined,
): readonly string[] | undefined {
  const roles = usePreviewRole();
  return roles ?? realRoles;
}

// ─── "View as division" ─────────────────────────────────────────────────────
// The same store, for the other access axis. Independent of the role preview:
// an admin can preview one, the other, or both at once, which is the only way to
// see what a Factory user in ChamSys actually gets.

const DIVISION_KEY = 'atlas:preview-division';

const readDivision = (): DivisionId | null => {
  try {
    return (sessionStorage.getItem(DIVISION_KEY) as DivisionId | null) ?? null;
  } catch {
    return null;
  }
};

let previewDivision: DivisionId | null = readDivision();
const divisionListeners = new Set<() => void>();

export function setPreviewDivision(division: DivisionId | null): void {
  previewDivision = division;
  try {
    if (division) sessionStorage.setItem(DIVISION_KEY, division);
    else sessionStorage.removeItem(DIVISION_KEY);
  } catch {
    /* private mode — the in-memory value still drives this tab */
  }
  divisionListeners.forEach((notify) => notify());
}

function subscribeDivision(notify: () => void): () => void {
  divisionListeners.add(notify);
  return () => divisionListeners.delete(notify);
}

export function usePreviewDivision(): DivisionId | null {
  return useSyncExternalStore(
    subscribeDivision,
    () => previewDivision,
    () => previewDivision,
  );
}
